import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { geoFrance, professionalProfiles, professionalCategories, serviceCategories } from '@afficheservices/db/schema'
import { eq, sql, and } from 'drizzle-orm'
import { assertCityNotDomTom } from '~/lib/dom-tom.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema, CityPlaceSchema } from '~/components/structured-data'
import { CityMap } from '~/components/city-map'
import { CrossLinksHub } from '~/components/cross-links'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { SERVICE_LABELS } from '~/lib/programmatic-seo'
import {
  CLIMATE_NOTES,
  REGULATIONS_BY_DEPT,
  DEFAULT_REGULATION_NOTE,
  DEPT_FACTS,
  DEFAULT_DEPT_FACT,
  formatFr,
  formatResponseTime,
} from '~/lib/page-variants'
import { getNeighborCities } from '~/lib/city-data.server'
import { fetchCityServiceStats } from '~/lib/city-service-stats.server'
import { wikidataUrlForInsee, communeImageForInsee } from '~/lib/commune-wikidata.server'
import { ArrowRight, Award, Shield, Users, CheckCircle, ExternalLink } from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

// Map service slug to RGE qualification name
const RGE_QUALIF: Record<string, string> = {
  plomberie: 'QualiPAC / Qualibat Plomberie',
  electricite: 'Qualifelec RGE',
  chauffage: 'QualiPAC / QualiBois',
  climatisation: 'QualiPAC Froid',
  isolation: 'Qualibat RGE Isolation',
  toiture: 'Qualibat RGE Couverture',
  peinture: 'Qualibat RGE Peinture/Façade',
  menuiserie: 'Qualibat RGE Menuiserie',
  'panneaux-solaires': 'QualiPV / QualiSol',
  renovation: 'Qualibat RGE Offre Globale',
}

export async function loader({ params }: LoaderFunctionArgs) {
  const svcSlug = params.service ?? ''
  const citySlug = params.city ?? ''
  const svc = SERVICE_LABELS[svcSlug]
  if (!svc) throw new Response('Not Found', { status: 404 })

  const cityRows = await db
    .select({ name: geoFrance.communeName, postalCode: geoFrance.postalCode, dept: geoFrance.departmentCode, deptName: geoFrance.departmentName, region: geoFrance.regionName, population: geoFrance.population, lat: geoFrance.latitude, lon: geoFrance.longitude, communeCode: geoFrance.communeCode })
    .from(geoFrance)
    .where(sql`LOWER(REPLACE(REPLACE(TRANSLATE(${geoFrance.communeName},'àáâãäåèéêëìíîïòóôõöùúûüýÿñçÀÁÂÃÄÅÈÉÊËÌÍÎÏÒÓÔÕÖÙÚÛÜÝŸÑÇ','aaaaaaeeeeiiiioooooouuuuyyncAAAAAAEEEEIIIIOOOOOUUUUYYNC'),' ','-'),'''','-'))=${citySlug}`)
    .limit(1)
  if (cityRows.length === 0) throw new Response('Ville non trouvée', { status: 404 })
  const city = cityRows[0]
  // 410 only if every namesake is DOM-TOM (keeps mainland Saint-Denis etc.).
  await assertCityNotDomTom(citySlug)

  const [catRow] = await db.select({ id: serviceCategories.id }).from(serviceCategories).where(eq(serviceCategories.slug, svcSlug)).limit(1)
  let proCount = 0
  let topPros: Array<{ id: string; companyName: string; slug: string; city: string; ratingAvg: string | null; reviewCount: number }> = []
  if (catRow) {
    const [row] = await db.select({ count: sql<number>`count(DISTINCT ${professionalProfiles.id})::int` })
      .from(professionalProfiles)
      .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .where(and(eq(professionalCategories.categoryId, catRow.id), eq(professionalProfiles.departmentCode, city.dept)))
    proCount = Number(row?.count ?? 0)

    if (proCount > 0) {
      // Top-3 RGE pros uniquify each /rge/{svc}/{city} page — names diverge
      // across cities even when the rest of the template is identical.
      topPros = await db
        .select({
          id: professionalProfiles.id,
          companyName: professionalProfiles.companyName,
          slug: professionalProfiles.slug,
          city: professionalProfiles.city,
          ratingAvg: professionalProfiles.ratingAvg,
          reviewCount: professionalProfiles.reviewCount,
        })
        .from(professionalProfiles)
        .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
        .where(and(eq(professionalCategories.categoryId, catRow.id), eq(professionalProfiles.departmentCode, city.dept)))
        .orderBy(sql`${professionalProfiles.reviewCount} DESC NULLS LAST, ${professionalProfiles.companyName}`)
        .limit(3)
    }
  }

  const rgeQualif = RGE_QUALIF[svcSlug] ?? 'Qualibat RGE'

  // Per-city signals — neighbors + activity for content uniqueness.
  const lat = Number(city.lat)
  const lon = Number(city.lon)
  const [neighbors, stats] = await Promise.all([
    getNeighborCities(lat, lon, city.communeCode, 5, 30),
    catRow ? fetchCityServiceStats(catRow.id, city.name) : Promise.resolve(null),
  ])

  return {
    svc, svcSlug, city, proCount, topPros, citySlug, rgeQualif, neighbors, stats,
    wikidataUrl: wikidataUrlForInsee(city.communeCode),
    communeImageUrl: communeImageForInsee(city.communeCode),
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return []
  const { svc, city, rgeQualif, citySlug, svcSlug } = data
  const title = `${svc.artisan} RGE à ${city.name} — ${rgeQualif} | AfficheServices`
  const desc = `Trouvez ${svc.article} ${svc.artisan} certifié RGE (${rgeQualif}) à ${city.name} (${city.postalCode}). Artisans vérifiés pour bénéficier des aides MaPrimeRénov' et CEE.`
  return [
    { title }, { name: 'description', content: desc },
    // Thin templated tree (RGE qualification + city, no unique AI content) —
    // noindex,follow so Google stops flagging "Crawled - currently not indexed".
    { name: 'robots', content: 'noindex, follow' },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/rge/${svcSlug}/${citySlug}` },
    { property: 'og:title', content: title }, { property: 'og:description', content: desc },
    { property: 'og:image', content: `https://www.afficheservices.fr/og/rge/${svcSlug}/${citySlug}.svg` },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:type', content: 'image/svg+xml' },
  ]
}

export default function RGEServiceCityPage() {
  const { svc, svcSlug, city, proCount, topPros, rgeQualif, citySlug, neighbors, stats, wikidataUrl, communeImageUrl } = useLoaderData<typeof loader>()
  const climate = CLIMATE_NOTES[city.region]
  const regulation = REGULATIONS_BY_DEPT[city.dept] ?? DEFAULT_REGULATION_NOTE
  const deptFact = DEPT_FACTS[city.dept] ?? DEFAULT_DEPT_FACT
  const responseTime = stats ? formatResponseTime(stats.avgResponseHours) : null
  // Population label varies the page text per city — guards against
  // 0% uniqueness across same-service-different-city programmatic pages.
  const popLabel = city.population
    ? city.population > 100000 ? `grande ville de ${(city.population / 1000).toFixed(0)}k habitants`
    : city.population > 20000 ? `commune de ${(city.population / 1000).toFixed(0)}k habitants`
    : `commune de ${formatFr(city.population)} habitants`
    : `commune du ${city.deptName}`

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'RGE', url: 'https://www.afficheservices.fr/rge' },
        { name: svc.name, url: `https://www.afficheservices.fr/services/${svcSlug}` },
        { name: city.name, url: `https://www.afficheservices.fr/rge/${svcSlug}/${citySlug}` },
      ]} />
      <CityPlaceSchema
        city={{
          name: city.name,
          postalCode: city.postalCode,
          dept: city.dept,
          deptName: city.deptName,
          region: city.region,
          lat: city.lat,
          lon: city.lon,
        }}
        service={{ name: `${svc.name} RGE`, slug: svcSlug }}
        canonicalUrl={`https://www.afficheservices.fr/rge/${svcSlug}/${citySlug}`}
        priceBand={stats?.priceBand}
        offerCount={proCount}
        wikidataUrl={wikidataUrl}
        communeImageUrl={communeImageUrl}
      />

      <section className="bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-emerald-200">
            <Award className="h-3.5 w-3.5" /> {rgeQualif}
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">
            {svc.artisan.charAt(0).toUpperCase() + svc.artisan.slice(1)} RGE à {city.name}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-emerald-200">
            {proCount > 0
              ? `${proCount} ${svc.artisan}s certifiés RGE dans le ${city.deptName}. Éligibles MaPrimeRénov' et primes CEE.`
              : `Trouvez ${svc.article} ${svc.artisan} RGE à ${city.name} pour bénéficier des aides à la rénovation.`}
          </p>
          <Link to="/devis-gratuit" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 text-sm font-bold text-emerald-900 hover:bg-emerald-50">
            3 devis RGE gratuits <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-12 lg:px-6">
        <div className="mb-8">
          <CityMap lat={Number(city.lat)} lon={Number(city.lon)} cityName={city.name} height={240} />
        </div>

        <section>
          <h2 className="text-xl font-bold text-slate-900">Pourquoi choisir un {svc.artisan} RGE à {city.name} ?</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            Un artisan RGE (Reconnu Garant de l’Environnement) est la condition sine qua non pour bénéficier
            des aides à la rénovation énergétique : MaPrimeRénov’, primes CEE, écoPTZ, TVA à 5,5 %.
            À {city.name}, la qualification <strong>{rgeQualif}</strong> couvre les travaux de {svc.name.toLowerCase()}.
            Sans artisan RGE, aucune de ces aides n’est accessible.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            {city.name} ({city.postalCode}) est une {popLabel} dans le {city.deptName} ({city.dept}), région {city.region}.
            {proCount > 0
              ? ` ${proCount} ${svc.artisan}s certifiés RGE y opèrent et sont éligibles aux aides en vigueur.`
              : ` Les ${svc.artisan}s RGE du ${city.deptName} interviennent sous 48h.`}
          </p>
          {climate && (
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              <strong>Spécificité régionale :</strong> {climate}
            </p>
          )}
        </section>

        {stats && stats.recentLeads > 0 && (
          <section className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-sm font-semibold text-emerald-900">
              📈 Demandes RGE {city.deptName} ({city.dept}) sur 30 jours
            </p>
            <p className="mt-1 text-sm text-emerald-800">
              <strong>{formatFr(stats.recentLeads)} dossiers</strong>
              {responseTime ? ` — réponse moyenne ${responseTime}` : ''}
              {stats.proCount > 0 ? ` · ${stats.proCount} artisans RGE actifs` : ''}.
            </p>
          </section>
        )}

        <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            ⚖️ Réglementation locale ({city.dept})
          </p>
          <p className="mt-1 text-sm leading-relaxed text-amber-800">{regulation}</p>
        </section>

        <section className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-900">
            📊 Le saviez-vous ? ({city.deptName})
          </p>
          <p className="mt-1 text-sm leading-relaxed text-slate-700">{deptFact}</p>
        </section>

        {topPros.length > 0 && (
          <section className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
            <h2 className="text-lg font-bold text-emerald-900">
              Top {topPros.length} {svc.artisan}{topPros.length > 1 ? 's' : ''} RGE à {city.name} et {city.deptName}
            </h2>
            <ul className="mt-4 space-y-2">
              {topPros.map((pro, i) => (
                <li key={pro.id} className="flex items-start gap-3 text-sm">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-xs font-bold text-white">{i + 1}</span>
                  <div className="flex-1">
                    <Link to={`/pro/${pro.slug}`} className="font-semibold text-emerald-900 hover:text-emerald-700">
                      {pro.companyName}
                    </Link>
                    <span className="ml-2 text-xs text-emerald-700/70">— {pro.city}</span>
                    {Number(pro.reviewCount) > 0 && pro.ratingAvg && (
                      <span className="ml-2 text-xs text-amber-600">★ {pro.ratingAvg} ({pro.reviewCount} avis)</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-emerald-700/70">
              SIRET vérifié · {rgeQualif} · intervention dans le {city.deptName}.
            </p>
          </section>
        )}

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {[
            { icon: Shield, title: 'MaPrimeRénov\'', desc: `Jusqu'à 11 000 € pour une PAC, 25 €/m² pour l'isolation. Obligatoire : artisan RGE.` },
            { icon: Award, title: 'Primes CEE', desc: `Jusqu'à 5 500 € cumulables. Engagement CEE signé AVANT le devis.` },
            { icon: CheckCircle, title: 'TVA 5,5 %', desc: 'Au lieu de 10 % pour les travaux d\'amélioration énergétique avec RGE.' },
            { icon: Users, title: 'ÉcoPTZ', desc: 'Prêt à taux zéro jusqu\'à 50 000 € sur 20 ans pour bouquets de travaux RGE.' },
          ].map((b) => (
            <div key={b.title} className="rounded-xl border border-border bg-white p-5 shadow-xs">
              <b.icon className="h-6 w-6 text-emerald-600" />
              <h3 className="mt-2 text-sm font-bold text-slate-900">{b.title}</h3>
              <p className="mt-1 text-xs text-slate-500">{b.desc}</p>
            </div>
          ))}
        </div>

        <section className="mt-12 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="flex items-center gap-2 text-lg font-bold text-emerald-900">
            <CheckCircle className="h-5 w-5" /> Vérifier un artisan RGE
          </h2>
          <p className="mt-2 text-sm text-emerald-800">
            Avant de signer un devis, vérifiez la qualification RGE sur l'annuaire officiel France Rénov'.
            L'artisan doit être certifié pour le geste exact à la date des travaux.
          </p>
          <a href="https://france-renov.gouv.fr/annuaire-rge" target="_blank" rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800">
            Annuaire officiel RGE <ExternalLink className="h-4 w-4" />
          </a>
        </section>

        <div className="mt-12">
          <CrossLinksHub
            primarySlug={svcSlug}
            cityName={city.name}
            citySlug={citySlug}
            currentRoute="rge"
          />
        </div>

        {neighbors.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-slate-900">Artisans RGE dans les communes voisines</h2>
            <p className="mt-2 text-sm text-slate-600">
              Mêmes qualifications RGE, artisans proches dans un rayon de 30 km autour de {city.name} :
            </p>
            <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {neighbors.map((n) => (
                <li key={n.postalCode + n.name}>
                  <Link
                    to={`/rge/${svcSlug}/${n.name.toLowerCase().replace(/['\s]/g, '-')}`}
                    className="block rounded-lg border border-slate-200 bg-white p-2.5 text-sm hover:border-primary hover:bg-slate-50"
                  >
                    <span className="font-medium text-slate-900">{n.name}</span>
                    <span className="ml-1 text-xs text-slate-500">({n.km.toFixed(0)} km)</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-12 text-center">
          <Link to="/devis-gratuit" className="inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3.5 text-sm font-bold text-white hover:bg-primary-hover">
            Demander 3 devis RGE à {city.name} <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  )
}
